import React, { useState, useRef, useMemo } from 'react';
import {
  Box, Grid, Alert, Typography, FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useRouteMatch, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import RfqLineItemsEditor from '../../components/material/RfqLineItemsEditor';
import RfqVendorSelector from '../../components/material/RfqVendorSelector';
import RfqAttachmentsPanel from '../../components/material/RfqAttachmentsPanel';
import RfqPublishReviewDialog from '../../components/material/RfqPublishReviewDialog';
import { createRfqWithItemsAndVendors } from '../../api/rfq';
import useItemNames from '../../hooks/useItemNames';
import { RFQ_TYPES } from '../../constants/rfqManagement';
import {
  emptyRfqLine, toRfqCreatePayload, validateRfqDates, findDuplicateItemRows,
} from '../../utils/rfqEntity';
import { summarizeRfqInviteNotify } from '../../utils/rfqInviteNotify';
import { generateTxnNumber } from '../../utils';
import { IPD_COLORS } from '../../theme';

const FORM_ID = 'rfq-form';

export default function RfqForm() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const preselectedVendorId = useMemo(
    () => new URLSearchParams(location.search).get('vendor_id') || '',
    [location.search],
  );
  const listUrl = url.replace('/rfq/new', '/rfqs');
  const detailBase = url.replace('/rfq/new', '/rfqs');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [vendorError, setVendorError] = useState(null);
  const [lines, setLines] = useState([{ ...emptyRfqLine() }]);
  const [vendorIds, setVendorIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const submitModeRef = useRef('draft');

  const { getBaseUomCode, getPurchaseUomCode, getGstRate, resolveItemId } = useItemNames();

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      rfq_number: generateTxnNumber('RFQ'),
      rfq_title: '',
      rfq_type: 'STANDARD',
      issue_date: dayjs().format('YYYY-MM-DDTHH:mm'),
      submission_deadline: dayjs().add(7, 'day').hour(18).minute(0).format('YYYY-MM-DDTHH:mm'),
      minimum_required_quotes: 3,
      is_open_bid: true,
      is_sealed_bid: false,
      terms_and_conditions: '',
      notes: '',
    },
  });

  const formValues = watch();
  const submissionDeadline = formValues.submission_deadline;
  const typeOptions = RFQ_TYPES.map((t) => ({ value: t.value, label: t.label }));
  const validLineCount = lines.filter((l) => l.item_id && Number(l.required_quantity) > 0).length;
  const duplicateItemCount = findDuplicateItemRows(lines).size;
  const typeLabel = typeOptions.find((t) => t.value === formValues.rfq_type)?.label || formValues.rfq_type;

  function validateAll() {
    const dateErr = validateRfqDates(formValues.issue_date, formValues.submission_deadline);
    if (dateErr) return dateErr;
    if (validLineCount === 0) return 'Add at least one line item with item and quantity.';
    if (duplicateItemCount > 0) return 'The same item appears on more than one row. Remove the duplicates.';
    if (vendorIds.length === 0) {
      setVendorError('Select at least one approved vendor to invite.');
      return 'Select at least one approved vendor to invite.';
    }
    setSaveError(null);
    setVendorError(null);
    return null;
  }

  async function onSubmit(data) {
    const fieldsOk = await trigger([
      'rfq_number', 'rfq_title', 'rfq_type', 'issue_date', 'submission_deadline',
    ]);
    if (!fieldsOk) {
      const msg = 'Please fill all required RFQ fields.';
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    const validationMsg = validateAll();
    if (validationMsg) {
      setSaveError(validationMsg);
      toast.error(validationMsg);
      return;
    }

    const publish = submitModeRef.current === 'publish';
    submitModeRef.current = 'draft';
    setSaving(true);
    setSaveError(null);

    try {
      const submitData = { ...data };
      if (publish && submitData.issue_date && dayjs(submitData.issue_date).isBefore(dayjs(), 'minute')) {
        submitData.issue_date = dayjs().format('YYYY-MM-DDTHH:mm');
      }
      const payload = toRfqCreatePayload(submitData, { vendorIds, attachments, lines });
      const result = await createRfqWithItemsAndVendors(payload, lines, { publish });

      toast.success(publish
        ? `RFQ published. ${summarizeRfqInviteNotify(result.notify)}`
        : 'RFQ saved as draft.');

      const rfqId = result?.id || result?.rfq_id;
      history.push(rfqId
        ? `${detailBase}/${rfqId}${publish ? '?published=1' : '?saved=1'}`
        : listUrl);
    } catch (e) {
      const msg = e.message || 'Failed to save RFQ';
      setSaveError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function openPublishReview() {
    const fieldsOk = await trigger([
      'rfq_number', 'rfq_title', 'rfq_type', 'issue_date', 'submission_deadline',
    ]);
    if (!fieldsOk) {
      const msg = 'Please fill all required RFQ fields.';
      setSaveError(msg);
      toast.error(msg);
      return;
    }
    const validationMsg = validateAll();
    if (validationMsg) {
      setSaveError(validationMsg);
      toast.error(validationMsg);
      return;
    }
    setReviewOpen(true);
  }

  async function confirmPublish() {
    submitModeRef.current = 'publish';
    await onSubmit(getValues());
  }

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        eyebrow="Material Management"
        title="New Request for Quotation"
        description="Fill in details, line items, and vendors on this page, then save or publish."
        actions={(
          <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}
            onClick={() => history.push(listUrl)}>
            Exit
          </InvButton>
        )}
      />

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Box component="form" id={FORM_ID} noValidate onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SectionCard title="RFQ Details" subtitle="Title, dates, and commercial terms">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="rfq_number" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="RFQ Number *" disabled error={errors.rfq_number} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="rfq_type" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvSelect label="RFQ Type *" value={field.value} onChange={field.onChange}
                    options={typeOptions} error={errors.rfq_type} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller name="minimum_required_quotes" control={control}
                render={({ field }) => (
                  <InvTextField
                    {...field}
                    label="Minimum Quotes"
                    type="number"
                    inputProps={{ min: 1, step: 1 }}
                    helperText="Must be at least 1. Default is 3."
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="is_open_bid" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{ color: IPD_COLORS.primary }}
                      />
                    )}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          Open bid — allow unregistered vendors to bid
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          The public RFQ link will offer new vendors a quick registration.
                          Leave off and only invited vendors can quote.
                        </Typography>
                      </Box>
                    )}
                  />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="is_sealed_bid" control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{ color: IPD_COLORS.primary }}
                      />
                    )}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          Sealed bid — hide rates until the deadline
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          Nobody here can see quoted rates until the submission deadline
                          passes. Unsealing early is possible but is recorded with a reason.
                        </Typography>
                      </Box>
                    )}
                  />
                )} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller name="rfq_title" control={control}
                rules={{ required: 'Title is required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="RFQ Title *"
                    placeholder="e.g. Monthly Pharma Supply" error={errors.rfq_title} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller name="issue_date" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Issue Date *" type="datetime-local"
                    error={errors.issue_date} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Controller name="submission_deadline" control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <InvTextField {...field} label="Submission Deadline *" type="datetime-local"
                    error={errors.submission_deadline} />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="terms_and_conditions" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Terms & Conditions" multiline rows={4}
                    placeholder="Payment terms, delivery expectations, warranty…" />
                )} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="notes" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Internal Notes" multiline rows={2} />
                )} />
            </Grid>
          </Grid>
        </SectionCard>

        <RfqLineItemsEditor
          lines={lines}
          onChange={setLines}
          getBaseUomCode={getBaseUomCode}
          getPurchaseUomCode={getPurchaseUomCode}
          getGstRate={getGstRate}
          defaultRequiredByDate={submissionDeadline}
        />

        <RfqVendorSelector
          selectedIds={vendorIds}
          onChange={setVendorIds}
          preselectedId={preselectedVendorId}
          error={vendorError}
        />

        <RfqAttachmentsPanel attachments={attachments} onChange={setAttachments} />

        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          borderTop: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(8px)',
          px: { xs: 2, sm: 3 },
          py: 1.5,
        }}>
          <Box sx={{
            maxWidth: 1400,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
          }}>
            <InvButton type="submit" variant="outlined" tone="teal"
              loading={saving && !reviewOpen} onClick={() => { submitModeRef.current = 'draft'; }}>
              Save as Draft
            </InvButton>
            <InvButton type="button" tone="mint"
              disabled={saving} onClick={openPublishReview}>
              Publish RFQ
            </InvButton>
          </Box>
        </Box>
      </Box>

      <RfqPublishReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onPublish={confirmPublish}
        loading={saving}
        error={saveError}
        formId={FORM_ID}
        rfqNumber={formValues.rfq_number}
        title={formValues.rfq_title}
        typeLabel={typeLabel}
        issueDate={formValues.issue_date}
        submissionDeadline={formValues.submission_deadline}
        minimumQuotes={formValues.minimum_required_quotes}
        validLineCount={validLineCount}
        vendorCount={vendorIds.length}
        isOpenBid={formValues.is_open_bid}
        attachmentCount={attachments.filter((a) => a.file_name).length}
        itemLines={lines
          .filter((l) => l.item_id && l.required_quantity)
          .map((l) => `${resolveItemId(l.item_id)} — Qty ${l.required_quantity} ${l.uom_code || ''}${
            l.last_rate != null ? ` — last rate ${l.last_rate}` : ''
          }`)}
      />
    </Box>
  );
}
